import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  LinearProgress,
  Typography,
  DialogActions,
  DialogContent,
  CircularProgress
} from '@mui/material';
import { useState, useEffect } from 'react';
import { archLevel } from 'src/data/config';
import { useLazyGetEssentialDocsDashboardDataQuery } from 'src/services/SummaryDashboard';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { IEssentialDocsArtifacts, IEssentialDocs } from 'src/types/SummaryDashboard';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { useSelectedAppInfo } from 'src/util/SelectedAppInfo';
import { setMessage } from 'src/store/Message';
import { PopupComponent } from 'src/components/common/PopupComponent';

const EssentialDocumentsWidget = (props: { essentialWidgetStatic: IEssentialDocs }) => {
  const { essentialWidgetStatic } = props;
  const [essentialWidgetDataObj, setEssentialWidgetDataObj] = useState(essentialWidgetStatic);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { getDateTimeDiffWithCurrentUTC } = usePreferedDateFormat();

  const dispatchApp = useAppDispatch();
  const { selectedApp } = useSelectedAppInfo();
  const studyId = useAppSelector(state => state.site.selectedStudy);
  const selectedCountry = useAppSelector(state => state.site.selectedCountry);
  const selectedSite = useAppSelector(state => state.site.selectedSite);
  const appName = archLevel == 'site' ? process.env.REACT_APP_NAME_TEMP : process.env.REACT_APP_NAME;
  const [isWidgetDataLoading, setIsWidgetDataLoading] = useState(false);

  const [
    getEssentialDocsDashboardData,
    { error: essentialDocsDashboardError, isError: essentialDocsDashboardIsError }
  ] = useLazyGetEssentialDocsDashboardDataQuery();

  const getEssentialDocsWidgetData = async () => {
    setIsWidgetDataLoading(true);
    const essentialDocsWidgetsResponse: any = await getEssentialDocsDashboardData({
      site_id: selectedSite,
      study_id: studyId,
      country: selectedCountry,
      application: appName,
      application_section: selectedApp
    });
    if (essentialDocsWidgetsResponse.isSuccess) {
      setEssentialWidgetDataObj(essentialDocsWidgetsResponse.data);
    } else if (essentialDocsWidgetsResponse.isError) {
      setEssentialWidgetDataObj({} as IEssentialDocs);
    }
    setIsWidgetDataLoading(false);
  };

  useEffect(() => {
    setEssentialWidgetDataObj(essentialWidgetStatic);
    getEssentialDocsWidgetData();
  }, [selectedSite]);

  useEffect(() => {
    if (essentialDocsDashboardIsError) {
      setEssentialWidgetDataObj({} as IEssentialDocs);
      const err: any = essentialDocsDashboardError;
      if (err?.status == 500) {
        dispatchApp(
          setMessage({
            msgDetails: `${err?.status} : Internal Server Error`,
            severity: 'error'
          })
        );
      } else if (err?.status == 403) {
        dispatchApp(
          setMessage({
            msgDetails: 'Error 403 Restricted User permission',
            severity: 'error'
          })
        );
      }
    }
  }, [essentialDocsDashboardIsError]);

  const showEssentialDocsWidget = (essentialWidgetObj: IEssentialDocs, isInPopup = false) => {
    return (
      <>
        {essentialWidgetObj && (
          <Container style={{ paddingLeft: '5px', paddingRight: '15px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700} style={{ display: 'flex', alignItems: 'center' }}>
                {essentialWidgetObj.name}
                {isWidgetDataLoading && (
                  <CircularProgress size="1.5rem" style={{ position: 'relative', left: '5px' }} />
                )}
                <ArrowForwardIcon style={{ marginLeft: '5px', color: '#243F81' }} />
              </Typography>
              <Typography variant="body1">{`${essentialWidgetObj?.percentage}%`}</Typography>
            </Box>
            <Box mt={2}>
              <LinearProgress
                variant="determinate"
                value={essentialWidgetObj.percentage}
                style={{ height: 8, borderRadius: 5, backgroundColor: '#efefef' }}
              />
            </Box>
            {essentialWidgetObj?.artifacts?.length ? (
              essentialWidgetObj?.artifacts?.map(
                (artifact: IEssentialDocsArtifacts, index: number) =>
                  (isInPopup || index < 2) && (
                    <Box mt={2} key={artifact.id}>
                      <Grid container spacing={2} justifyContent="space-between">
                        <Grid item xs={1} style={{ display: 'flex', alignItems: 'center', paddingLeft: '30px' }}>
                          <InsertDriveFileIcon style={{ color: '#243F81' }} />
                        </Grid>
                        <Grid item xs={8} style={{ paddingLeft: '20px' }}>
                          <Typography variant="h6" style={{ color: '#243F81' }}>
                            {artifact.name}
                          </Typography>
                          <Typography variant="body1">{artifact.status}</Typography>
                        </Grid>
                        <Grid item xs={3} style={{ textAlign: 'right' }}>
                          {`${getDateTimeDiffWithCurrentUTC(artifact.updated_on)} ago`}
                        </Grid>
                      </Grid>
                    </Box>
                  )
              )
            ) : (
              <Box mt={2} mb={2}>
                <Alert severity="info">No Pending Documents</Alert>
              </Box>
            )}
          </Container>
        )}
      </>
    );
  };
  return (
    <>
      {showEssentialDocsWidget(essentialWidgetDataObj)}
      {essentialWidgetDataObj?.artifacts?.length > 0 && (
        <Button
          onClick={() => setIsPopupOpen(true)}
          style={{ display: 'flex', alignItems: 'flex-end', marginLeft: 'auto', marginRight: '5px' }}
        >
          View More
        </Button>
      )}
      <PopupComponent
        aria-labelledby="customized-dialog-title"
        open={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
      >
        <DialogContent style={{ width: '600px' }}>
          {showEssentialDocsWidget(essentialWidgetDataObj, true)}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPopupOpen(false)}>Close</Button>
        </DialogActions>
      </PopupComponent>
    </>
  );
};

export default EssentialDocumentsWidget;
